import type { SalaryDayNode } from '@/modules/activities'
import type { SalaryDayForm } from '@/modules/activities/components/overview/SalaryDayPrompt'
import { SalaryDayPrompt } from '@/modules/activities/components/overview/SalaryDayPrompt'
import { useSalaryDayMutations } from '@/modules/activities/mutations.hooks'
import { usePrompt } from '@ur-solutions/react-components'

export type SalaryDayFields = Pick<SalaryDayNode, 'partOfDay' | 'salaryCode'>

interface Anchor {
  workPeriodId?: string | null
  restPeriodId?: string | null
  /** Above 1 for a rest: one per night it covers. */
  maxPartOfDay?: number
  salaryDay?: SalaryDayFields | null
}

/**
 * The salary-day counterpart of `useEditDriverDiet`, and split out for the same
 * reason: the mutation reaches the settings store through its error handler,
 * which the prompt portal renders outside of, so the write has to stay with a
 * caller in the normal tree.
 */
export function useEditSalaryDay(userId: string, onChange?: () => void) {
  const addPrompt = usePrompt()
  const salaryDayMutations = useSalaryDayMutations()

  return async function editSalaryDay({
    workPeriodId = null,
    restPeriodId = null,
    maxPartOfDay = 1,
    salaryDay,
  }: Anchor) {
    const { data } = await addPrompt<SalaryDayForm | null>(resolve => (
      <SalaryDayPrompt
        initialData={{
          userId,
          salaryCodeId: salaryDay?.salaryCode?.id ?? null,
          partOfDay: salaryDay?.partOfDay ?? 1,
        }}
        maxPartOfDay={maxPartOfDay}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    await salaryDayMutations.updateOrCreateSalaryDay({
      variables: {
        input: { ...data, workPeriod: workPeriodId, restPeriod: restPeriodId },
      },
    })
    onChange?.()
  }
}
